import { isCvinExpansion } from "../projectGridColumns";
import { formatFootageNumber } from "./ExpansionFootageProgressBar";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import { parseFootageNumber } from "./expansionGroupUtils";
import {
  conduitFootageDenominator,
  fiberFootageDenominator,
} from "./expansionProgressUtils";

/** Footage targets for contract project picker (conduit + fiber). */
export function projectFootageTargets(row) {
  if (!row) return { conduit: null, fiber: null };
  if (isCvinExpansion(row)) {
    const sub = readSubsegmentSummaryFromAcf(row.acf_groups_json);
    return {
      conduit: conduitFootageDenominator(sub),
      fiber: fiberFootageDenominator(sub),
    };
  }
  const ag = row.acf_groups_json && typeof row.acf_groups_json === "object" ? row.acf_groups_json : {};
  const eng = ag.engineering_details || {};
  const sub = ag.subsegment_summary || {};
  const cons = ag.construction_details || {};
  const conduit =
    parseFootageNumber(eng.construction_footage) ??
    parseFootageNumber(sub.construction_footage) ??
    parseFootageNumber(cons.construction_footage) ??
    parseFootageNumber(cons.reported_footage);
  const fiber =
    parseFootageNumber(sub.override_footage) ??
    parseFootageNumber(eng.construction_footage) ??
    parseFootageNumber(sub.construction_footage) ??
    parseFootageNumber(cons.reported_footage);
  return { conduit: conduit > 0 ? conduit : null, fiber: fiber > 0 ? fiber : null };
}

export function formatProjectFootageLine(row) {
  const { conduit, fiber } = projectFootageTargets(row);
  const parts = [];
  if (conduit != null && conduit > 0) {
    parts.push(`Conduit ${formatFootageNumber(conduit)} ft`);
  }
  if (fiber != null && fiber > 0) {
    const showFiber =
      conduit == null || conduit <= 0 || Math.abs(fiber - conduit) > 0.01;
    if (showFiber) parts.push(`Fiber ${formatFootageNumber(fiber)} ft`);
  }
  if (!parts.length) return null;
  return parts.join(" · ");
}

export function sumFootageTargets(rows) {
  let conduit = 0;
  let fiber = 0;
  let hasConduit = false;
  let hasFiber = false;
  for (const row of rows) {
    const t = projectFootageTargets(row);
    if (t.conduit != null && t.conduit > 0) {
      conduit += t.conduit;
      hasConduit = true;
    }
    if (t.fiber != null && t.fiber > 0) {
      fiber += t.fiber;
      hasFiber = true;
    }
  }
  return {
    conduit: hasConduit ? conduit : null,
    fiber: hasFiber ? fiber : null,
  };
}

export function formatFootageTotals(rows) {
  const t = sumFootageTargets(rows);
  const parts = [];
  if (t.conduit != null) parts.push(`Conduit ${formatFootageNumber(t.conduit)} ft`);
  if (t.fiber != null) parts.push(`Fiber ${formatFootageNumber(t.fiber)} ft`);
  if (!parts.length) return null;
  return parts.join(" · ");
}

/**
 * Selected footage for a contract, honoring per-link coveredFootage overrides.
 * When coverage is set, that value is used once (not doubled for construction+fiber).
 * When blank, falls back to full conduit/fiber targets for the checked roles.
 */
export function formatContractSelectedFootage(rows, projectLinks) {
  const byId = new Map((rows || []).map((row) => [Number(row.id), row]));
  let conduit = 0;
  let fiber = 0;
  let coverage = 0;
  let hasConduit = false;
  let hasFiber = false;
  let hasCoverage = false;

  for (const link of projectLinks || []) {
    const row = byId.get(Number(link.projectId));
    if (!row) continue;
    const covered =
      link.coveredFootage != null && Number.isFinite(Number(link.coveredFootage))
        ? Number(link.coveredFootage)
        : null;
    if (covered != null && covered > 0) {
      coverage += covered;
      hasCoverage = true;
      continue;
    }
    const t = projectFootageTargets(row);
    if (link.appliesConstruction !== false && t.conduit != null && t.conduit > 0) {
      conduit += t.conduit;
      hasConduit = true;
    }
    if (link.appliesFiber !== false && t.fiber != null && t.fiber > 0) {
      fiber += t.fiber;
      hasFiber = true;
    }
  }

  const parts = [];
  if (hasCoverage) parts.push(`Coverage ${formatFootageNumber(coverage)} ft`);
  if (hasConduit) parts.push(`Conduit ${formatFootageNumber(conduit)} ft`);
  if (hasFiber) parts.push(`Fiber ${formatFootageNumber(fiber)} ft`);
  if (!parts.length) return null;
  return parts.join(" · ");
}

export function contractTableContractorLabel(contract) {
  if (contract?.status === "cancelled") return "Cancelled";
  if (contract?.status !== "awarded") return "Pending Award";
  const construction = String(contract.contractor || "").trim();
  const fiber = String(contract.fiberContractor || "").trim();
  if (construction) return construction;
  if (fiber) return fiber;
  return "—";
}
